import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridEventListener } from '@mui/x-data-grid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { majorApi, MajorSchema, MajorYearStatsSchema } from '../../../services';
import { useErrorHandler } from '../../../lib/errorHandler';
import { useAppSelector } from '../../../store/hooks';
import z from 'zod';
import { MOBILE_BREAKPOINT, ROW_HEIGHTS, LIST_HEIGHTS } from '../../../utils/responsive';
import { useEntityListState, createEntityToolbar, type StatsQueryParams } from '../../../hooks';

type Major = z.infer<typeof MajorSchema>;
type MajorYearStats = z.infer<typeof MajorYearStatsSchema>;
import { BaseDataGrid, PageHeader, MajorDialog, DeleteConfirmDialog, PersistentTabs, TabConfig } from '../../../components';
import { MajorsChart } from './MajorsChart';

const MajorsToolbar = createEntityToolbar({
	searchPlaceholder: 'Tìm kiếm ngành học...',
	exportFn: (params) => majorApi.exportToExcel(params),
});
import { useMajorsColumns, useMajorsQuery } from '../hooks';

interface MajorsContentProps {
	isAdmin: boolean;
	canEdit: boolean;
}

export default function MajorsContent({ isAdmin, canEdit }: MajorsContentProps) {
	const { showError, showSuccess } = useErrorHandler();
	const queryClient = useQueryClient();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);

	const {
		searchValue, paginationModel, sortModel, filterModel,
		handlePaginationModelChange, handleSortModelChange,
		handleFilterModelChange, handleSearch,
	} = useEntityListState({ defaultSortField: 'tenNganh' });

	// Dialog states
	const [majorDialog, setMajorDialog] = useState<{
		open: boolean;
		mode: 'create' | 'edit' | 'view';
		major: Major | null;
	}>({ open: false, mode: 'create', major: null });
	const dialogRef = useRef(majorDialog);
	dialogRef.current = majorDialog;

	const [deleteDialog, setDeleteDialog] = useState<{
		open: boolean;
		major: Major | null;
	}>({ open: false, major: null });

	// Use query hook with pagination and sorting
	const queryParams: StatsQueryParams = {
		filters: {
			query: searchValue,
			maKhoa: selectedFaculty ? [selectedFaculty] : undefined
		},
		page: paginationModel.page,
		pageSize: paginationModel.pageSize,
		sortBy: sortModel.length > 0 ? sortModel[0].field : undefined,
		sortOrder: (sortModel.length > 0 ? sortModel[0].sort : undefined) as 'asc' | 'desc' | undefined,
	};
	const { data: majorsData, isLoading, refetch } = useMajorsQuery(queryParams);
	const exportParams = useMajorsQuery.buildExportParams(queryParams);

	const majors = useMemo(() => majorsData?.data.stats || [], [majorsData?.data.stats]);
	const totalCount = majorsData?.pagination?.total || 0;

	// Sync URL params with dialog state
	useEffect(() => {
		const action = searchParams.get('action') as 'create' | 'edit' | 'view' | null;
		const majorId = searchParams.get('id');

		// Security: Redirect unauthorized users from edit/create to view mode
		if (action && ['create', 'edit'].includes(action) && !canEdit) {
			if (majorId) {
				setSearchParams({ action: 'view', id: majorId });
			} else {
				setSearchParams({});
			}
			return;
		}

		if (action && ['create', 'edit', 'view'].includes(action)) {
			if (action === 'create') {
				if (!dialogRef.current.open || dialogRef.current.mode !== 'create') {
					setMajorDialog({
						open: true,
						mode: 'create',
						major: null,
					});
				}
			} else if (majorId && majors.length > 0) {
				if (dialogRef.current.open
					&& dialogRef.current.major?.maNganh === majorId
					&& dialogRef.current.mode === action) {
					return;
				}
				const major = majors.find((m) => m.maNganh === majorId);
				if (major) {
					const majorWithStats = major as MajorYearStats;
					const dialogMajor = {
						...major,
						chiTieu: majorWithStats.quotaData || {},
					} as Major;
					setMajorDialog({
						open: true,
						mode: action,
						major: dialogMajor,
					});
				}
			}
		} else if (!action && dialogRef.current.open) {
			setMajorDialog({ open: false, mode: 'create', major: null });
		}
	}, [searchParams, majors, canEdit, setSearchParams]);

	// Delete mutation
	const deleteMutation = useMutation({
		mutationFn: majorApi.deleteMajor,
		onSuccess: () => {
			showSuccess('Xóa ngành thành công');
			queryClient.invalidateQueries({ queryKey: ['majors'] });
			setDeleteDialog({ open: false, major: null });
		},
		onError: (error: Error) => {
			showError(error.message || 'Có lỗi xảy ra khi xóa ngành');
		},
	});

	const handleRefresh = useCallback(() => {
		// Prevent multiple rapid calls
		if (isLoading) return;

		refetch();
	}, [isLoading, refetch]);

	const handleCreate = useCallback(() => {
		setSearchParams({ action: 'create' });
	}, [setSearchParams]);

	const handleView = useCallback(
		(major: Major) => () => {
			setSearchParams({ action: 'view', id: major.maNganh });
		},
		[setSearchParams],
	);

	const handleEdit = useCallback(
		(major: Major) => () => {
			setSearchParams({ action: 'edit', id: major.maNganh });
		},
		[setSearchParams],
	);

	const handleDelete = useCallback(
		(major: Major) => () => {
			setDeleteDialog({
				open: true,
				major,
			});
		},
		[],
	);

	const handleDeleteConfirm = useCallback(() => {
		// Prevent double submission
		if (deleteMutation.isPending || !deleteDialog.major) return;

		deleteMutation.mutate(deleteDialog.major.maNganh);
	}, [deleteDialog.major, deleteMutation]);

	const handleCloseMajorDialog = useCallback(() => {
		if (searchParams.has('action')) {
			setSearchParams({});
		}
		setMajorDialog({ open: false, mode: 'create', major: null });
	}, [searchParams, setSearchParams]);

	const handleCloseDeleteDialog = useCallback(() => {
		setDeleteDialog({ open: false, major: null });
	}, []);

	const handleRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
		if (canEdit) {
			handleEdit(params.row as Major)();
		} else {
			handleView(params.row as Major)();
		}
	}, [canEdit, handleEdit, handleView]);

	const columns = useMajorsColumns({
		isAdmin,
		isMobile,
		majorStats: majorsData?.data,
		onEdit: handleEdit,
		onDelete: handleDelete
	});

	const tabs: TabConfig[] = [
		{
			label: 'Danh sách',
			content: (
				<Box sx={{
					flex: 1,
					minHeight: 0,
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					display: 'flex',
					flexDirection: 'column'
				}}>
					<BaseDataGrid
						rows={majors}
						columns={columns}
						loading={isLoading}
						onRowClick={handleRowClick}
						getRowId={(row) => row.maNganh}
						rowCount={totalCount}
						paginationModel={paginationModel}
						onPaginationModelChange={handlePaginationModelChange}
						sortModel={sortModel}
						onSortModelChange={handleSortModelChange}
						filterModel={filterModel}
						onFilterModelChange={handleFilterModelChange}
						pageSizeOptions={[10, 25, 50, 100]}
						paginationMode="server"
						sortingMode="server"
						filterMode="server"
						disableRowSelectionOnClick
						rowHeight={isMobile ? undefined : ROW_HEIGHTS.DESKTOP}
						getRowHeight={isMobile ? () => 'auto' : undefined}
						slots={{
							toolbar: () => (
								<MajorsToolbar
									searchValue={searchValue}
									onSearch={handleSearch}
									onRefresh={handleRefresh}
									exportParams={exportParams}
									isMobile={isMobile}
								/>
							),
						}}
					/>
				</Box>
			)
		},
		{
			label: 'Biểu đồ thống kê',
			content: (
				<Box sx={{
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					overflow: 'auto'
				}}>
					<MajorsChart height={500} />
				</Box>
			)
		}
	];

	return (
		<Box sx={{
			width: '100%',
			height: '100vh',
			display: 'flex',
			flexDirection: 'column',
			overflow: 'hidden',
			p: 0
		}}>
			<PageHeader
				title="Danh sách Ngành"
				onRefresh={handleRefresh}
				onAdd={canEdit ? handleCreate : undefined}
				isAdmin={isAdmin}
				loading={isLoading}
			/>

			<PersistentTabs
				pageKey="majors"
				tabs={tabs}
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					minHeight: 0
				}}
			/>

			<MajorDialog
				open={majorDialog.open}
				onClose={handleCloseMajorDialog}
				major={majorDialog.major}
				mode={majorDialog.mode}
			/>

			<DeleteConfirmDialog
				open={deleteDialog.open}
				onClose={handleCloseDeleteDialog}
				onConfirm={handleDeleteConfirm}
				title="Xóa ngành"
				message={`Bạn có chắc chắn muốn xóa ngành "${deleteDialog.major?.tenNganh}"? Hành động này không thể hoàn tác.`}
				isLoading={deleteMutation.isPending}
			/>
		</Box>
	);
}